// src/hooks/useAdminModule.ts
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import {
  setNewModule,
  resetModule,
  setUpdateModule,
  setModulesWithoutFormat,
  addSelectedModule,
  removeSelectedModule,
} from 'src/redux/IAM/AdminModules/adminModulesSlice'
import {
  getModules,
  createModule,
  deleteModule,
  updateModule,
  getModulesWithoutFormat,
} from 'src/services/IAM/AdminModule/AdminModule.service'
import { useAlert } from 'src/hooks/useAlert'
import { usePagination } from '../Components/Pagination/usePagination'
import { IModule } from 'src/components/Sidebar/Sidebar.Interface'
import useLoading from 'src/hooks/useLoading'

/**
 * Custom hook to manage admin modules in the application.
 * It provides functions for loading, creating, updating, and deleting modules.
 * 
 * @returns An object containing module state and actions.
 */
export const useAdminModule = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { handleTotalItems, currentPage, rowsPerPage } = usePagination()
  const {
    modules,
    selectedModules,
    newModule,
    updatedModule,
    modulesWithoutFormat,
    module,
    loading,
    error,
  } = useSelector((state: RootState) => state.modules)

  /**
   * Loads modules with pagination.
   * 
   * @param {Object} options - Pagination options.
   * @param {number} [options.currentPage=1] - The current page to load.
   * @param {number} [options.pageSize=25] - The number of items per page.
   */
  const loadModules = ({
    currentPage = 1,
    pageSize = 25,
  }: {
    currentPage?: number
    pageSize?: number
  } = {}) => {
    try {
      startLoading()
      dispatch(
        getModules({
          currentPage,
          pageSize,
        }),
      )
        .then((result: any) => {
          stopLoading()
          if (getModules.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al crear la app',
            )
          } else if (getModules.fulfilled.match(result)) {
            showAlert('success', 'Módulos obtenidos correctamente')
            if (
              {
                currentPage,
                pageSize,
              }
            ) {
              handleTotalItems(result.payload.details)
            }
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener los permisos')
    }
  }

  /**
   * Updates a module.
   * 
   * @param {IModule} module - The module to update.
   */
  const updateModuleAdmin = (module: IModule) => {
    dispatch(updateModule(module))
      .then((result: any) => {
        stopLoading()
        if (updateModule.rejected.match(result)) {
          showAlert('error', String(result.payload) || 'Error al crear la app')
        } else if (updateModule.fulfilled.match(result)) {
          loadModules()
          loadModulesWithoutFormat({
            currentPage,
            pageSize: rowsPerPage,
          })
          showAlert('success', 'Módulos obtenidos correctamente')
        }
      })
      .finally(() => {
        stopLoading()
      })
  }

  /**
   * Removes a module by ID.
   * 
   * @param {number} id - The ID of the module to remove.
   */
  const removeModule = (id: number) => {
    try {
      startLoading()
      dispatch(deleteModule(id))
        .then((result: any) => {
          stopLoading()
          if (deleteModule.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al eliminar el permiso',
            )
          } else if (deleteModule.fulfilled.match(result)) {
            showAlert('success', 'Permiso eliminado correctamente')
            loadModules()
            loadModulesWithoutFormat({
              currentPage,
              pageSize: rowsPerPage,
            })
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al eliminar el permiso')
    }
  }

  /**
   * Loads modules without format for pagination.
   * 
   * @param {Object} pagination - Pagination options.
   * @param {number} [pagination.currentPage=1] - The current page to load.
   * @param {number} [pagination.pageSize=25] - The number of items per page.
   */
  const loadModulesWithoutFormat = (pagination: {
    currentPage?: number
    pageSize?: number
  }) => {
    try {
      startLoading()
      dispatch(getModulesWithoutFormat(pagination))
        .then((result: any) => {
          stopLoading()
          if (getModulesWithoutFormat.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al crear la app',
            )
          } else if (getModulesWithoutFormat.fulfilled.match(result)) {
            showAlert('success', 'Módulos obtenidos correctamente')
            if (pagination) {
              handleTotalItems(result.payload.details)
            }
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener los permisos')
    }
  }

  /**
   * Creates a new module.
   * 
   * @param {IModule} module - The module to create.
   */
  const createModuleAdmin = (module: IModule) => {
    try {
      startLoading()
      dispatch(createModule(module))
        .then((result: any) => {
          stopLoading()
          if (createModule.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al crear la app',
            )
          } else if (createModule.fulfilled.match(result)) {
            showAlert('success', 'App creada correctamente')
            loadModules()
            loadModulesWithoutFormat({
              currentPage,
              pageSize: rowsPerPage,
            })
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al crear el permiso')
    }
  }

  /**
   * Sets a new module to the state.
   * 
   * @param {IModule} module - The new module to set.
   */
  const setNewModuleAdmin = (module: IModule) => {
    dispatch(setNewModule(module))
  }

  /**
   * Adds a module to the selected modules list.
   * 
   * @param {IModule} module - The module to add.
   */
  const addModuleSelect = (module: IModule) => {
    dispatch(addSelectedModule(module))
  }

  /**
   * Removes a module from the selected modules list.
   * 
   * @param {IModule} module - The module to remove.
   */
  const removeModuleSelect = (module: IModule) => {
    dispatch(removeSelectedModule(module))
  }

  /**
   * Sets modules without formatting to the state.
   * 
   * @param {IModule[]} module - The array of modules to set.
   */
  const setModuleWithoutFormatAdmin = (module: IModule[]) => {
    dispatch(setModulesWithoutFormat(module))
  }

  /**
   * Sets a module to update in the state.
   * 
   * @param {IModule} module - The module to set for updating.
   */
  const setUpdateModuleAdmin = (module: IModule) => {
    dispatch(setUpdateModule(module))
  }

  /**
   * Clears all modules from the state.
   */
  const clearModules = () => {
    dispatch(resetModule())
  }

  return {
    modules,
    module,
    newModule,
    loading,
    error,
    updatedModule,
    modulesWithoutFormat,
    selectedModules,
    addModuleSelect,
    removeModuleSelect,
    loadModulesWithoutFormat,
    setModuleWithoutFormatAdmin,
    createModuleAdmin,
    setNewModuleAdmin,
    loadModules,
    setUpdateModuleAdmin,
    updateModuleAdmin,
    removeModule,
    clearModules,
  }
}
